import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tabs,
  Typography,
  Upload,
  message,
} from 'antd';
import type { UploadFile } from 'antd';
import { ArrowLeftOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import PageShell from '../Layout/PageShell';
import { categoryService } from '../../services/categoryService';
import { listingAPI, storeAPI } from '../../services/api';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchListingById } from '../../store/slices/listingsSlice';
import type { Category, Store } from '../../types';
import {
  AVAILABILITY_OPTIONS,
  CURRENCY_OPTIONS,
  ITEM_TYPE_OPTIONS,
  LISTING_FORM_INITIAL_VALUES,
  LISTING_TYPE_OPTIONS,
  PRICE_TYPE_OPTIONS,
  PRODUCT_CONDITION_OPTIONS,
  STATUS_OPTIONS,
  buildCreateListingBody,
  buildUpdateListingBody,
  imageUrlFromListingImage,
  listingDescriptionPlainText,
  mapListingToFormValues,
} from '../../utils/vendorListingForm';
import ListingDescriptionEditor from './ListingDescriptionEditor';

const { Text } = Typography;
const { TextArea } = Input;

type ImageRow = { uid: string; name: string; url: string; file?: File };

export type ListingFormMode = 'create' | 'edit';

export interface ListingFormProps {
  mode: ListingFormMode;
  listingId?: string;
}

const ListingForm: React.FC<ListingFormProps> = ({ mode, listingId: listingIdProp }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const { currentListing, loading: listingsLoading, error: listingsError } = useAppSelector((s) => s.listings);

  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [imageList, setImageList] = useState<ImageRow[]>([]);
  const [loadSettled, setLoadSettled] = useState(mode === 'create');

  const routeId = listingIdProp ?? '';
  const listingMatchesRoute = Boolean(
    mode === 'edit' && currentListing && routeId && currentListing._id === routeId
  );
  const showSpinner =
    mode === 'edit' && Boolean(routeId && (!loadSettled || (listingsLoading && !listingMatchesRoute)));

  const loadCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      const res = await categoryService.getCategories({ isActive: true });
      setCategories(res.categories || []);
    } catch {
      message.error('Failed to load categories');
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  const loadStores = useCallback(async () => {
    try {
      setStoresLoading(true);
      const response = await storeAPI.getMyStores();
      const list = (response.data as { data?: { stores?: Store[] } }).data?.stores ?? [];
      setStores(list);
    } catch {
      message.error('Failed to load stores');
      setStores([]);
    } finally {
      setStoresLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
    loadStores();
  }, [loadCategories, loadStores]);

  useEffect(() => {
    if (mode !== 'edit' || !routeId) {
      setLoadSettled(true);
      return;
    }
    setLoadSettled(false);
    dispatch(fetchListingById(routeId)).finally(() => setLoadSettled(true));
  }, [dispatch, mode, routeId]);

  useEffect(() => {
    if (mode !== 'edit' || !listingMatchesRoute || !currentListing) return;
    form.setFieldsValue({
      ...LISTING_FORM_INITIAL_VALUES,
      ...mapListingToFormValues(currentListing as Parameters<typeof mapListingToFormValues>[0]),
    });
    const li = currentListing as { images?: unknown[] };
    if (li.images?.length) {
      setImageList(
        li.images.map((img, i) => ({
          uid: `existing-${i}`,
          name: `image-${i + 1}`,
          url: imageUrlFromListingImage(img),
        }))
      );
    } else {
      setImageList([]);
    }
    const catId =
      typeof currentListing.category === 'string'
        ? currentListing.category
        : (currentListing.category as { _id?: string })?._id;
    if (catId) {
      const subs = categories.filter((cat) => {
        const p = cat.parentCategory;
        const pid = typeof p === 'string' ? p : p && typeof p === 'object' && '_id' in p ? String((p as { _id: string })._id) : undefined;
        return pid === catId;
      });
      setSubcategories(subs);
    }
  }, [mode, listingMatchesRoute, currentListing, form, categories]);

  const handleCategoryChange = (categoryId: string) => {
    form.setFieldsValue({ subcategory: undefined });
    if (!categoryId) {
      setSubcategories([]);
      return;
    }
    const subs = categories.filter((cat) => {
      const p = cat.parentCategory;
      const pid = typeof p === 'string' ? p : p && typeof p === 'object' && '_id' in p ? String((p as { _id: string })._id) : undefined;
      return pid === categoryId;
    });
    setSubcategories(subs);
  };

  const categoryOptions = categories.map((c) => ({ value: c._id, label: c.name }));
  const subcategoryOptions = subcategories.map((c) => ({ value: c._id, label: c.name }));
  const approvedStores = stores.filter((s) => s.verificationStatus === 'approved');
  const storeOptions = approvedStores.map((s) => ({
    value: s._id,
    label: s.storeName || 'Store',
  }));

  const uploadFileList: UploadFile[] = imageList.map((img) => ({
    uid: img.uid,
    name: img.name,
    status: 'done',
    url: img.url,
  }));

  const handleBeforeUpload = (file: File) => {
    if (imageList.length >= 8) {
      message.warning('Maximum 8 images');
      return Upload.LIST_IGNORE;
    }
    const uid = `${Date.now()}-${file.name}`;
    const url = URL.createObjectURL(file);
    setImageList((prev) => [...prev, { uid, name: file.name, url, file }]);
    return false;
  };

  const handleRemove = (file: UploadFile) => {
    setImageList((prev) => prev.filter((x) => x.uid !== file.uid));
  };

  const uploadNewFiles = async (id: string, files: File[]) => {
    if (!files.length) return;
    const formData = new FormData();
    files.forEach((f) => formData.append('images', f));
    await listingAPI.uploadImages(id, formData);
  };

  const onFinish = async (values: Record<string, unknown>) => {
    if (imageList.length === 0) {
      message.error('Please upload at least one image');
      return;
    }

    setSubmitting(true);
    try {
      const newFiles = imageList.filter((x) => x.file).map((x) => x.file!);

      if (mode === 'create') {
        const body = buildCreateListingBody(values);
        const { data } = await listingAPI.create(body);
        const created = (data as { data?: { listing?: { _id: string } } }).data?.listing;
        const id = created?._id;
        if (!id) throw new Error('Listing created but no id returned');
        await uploadNewFiles(id, newFiles);
        message.success('Listing created successfully. It will be reviewed before going live.');
        navigate('/listings');
        return;
      }

      if (!routeId) return;
      const body = buildUpdateListingBody(values);
      await listingAPI.update(routeId, body);
      await uploadNewFiles(routeId, newFiles);
      message.success('Listing updated successfully!');
      navigate('/listings');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(e.response?.data?.message || e.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const headerActions = (
    <Space wrap size={8} className="w-full min-[480px]:w-auto">
      <Button
        type="default"
        icon={<ArrowLeftOutlined />}
        className="w-full min-[480px]:w-auto border-slate-200 text-slate-700 hover:border-[#0171BF] hover:!text-[#0171BF]"
        onClick={() => navigate('/listings')}
      >
        My listings
      </Button>
    </Space>
  );

  if (mode === 'edit' && !routeId) {
    return (
      <PageShell title="Edit listing" description="Invalid link — no listing id." titleEnd={headerActions}>
        <Card className="vendor-surface-card max-w-lg rounded-xl border border-slate-200/80 shadow-sm">
          <Button type="primary" size="large" block onClick={() => navigate('/listings')}>
            Go to My listings
          </Button>
        </Card>
      </PageShell>
    );
  }

  if (showSpinner) {
    return (
      <PageShell
        title="Edit listing"
        description="Loading this listing so you can update it."
        titleEnd={headerActions}
      >
        <Card className="vendor-surface-card w-full border border-slate-200/80 shadow-sm rounded-xl">
          <div className="flex justify-center py-16">
            <Spin size="large" />
          </div>
        </Card>
      </PageShell>
    );
  }

  if (mode === 'edit' && loadSettled && !listingMatchesRoute) {
    return (
      <PageShell
        title="Listing not found"
        description="This listing may have been removed or the link is incorrect."
        titleEnd={headerActions}
      >
        <Card className="vendor-surface-card w-full max-w-lg rounded-xl border border-slate-200/80 shadow-sm">
          {listingsError ? <Alert type="error" message={listingsError} className="mb-4" showIcon /> : null}
          <Button type="primary" size="large" block onClick={() => navigate('/listings')}>
            Go to My listings
          </Button>
        </Card>
      </PageShell>
    );
  }

  const pricingDetailsTab = (
    <>
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={8}>
          <Form.Item name="price" label="Price" rules={[{ required: true, message: 'Please enter price' }]}>
            <InputNumber
              size="large"
              className="w-full max-w-full"
              min={0}
              placeholder="Enter price"
              controls={false}
              formatter={(value) => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => parseFloat(String(value ?? '').replace(/₹\s?|(,*)/g, '')) || (0 as any)}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="currency" label="Currency">
            <Select size="large" className="w-full" options={CURRENCY_OPTIONS} showSearch optionFilterProp="label" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="priceType" label="Price type">
            <Select size="large" className="w-full" options={PRICE_TYPE_OPTIONS} showSearch optionFilterProp="label" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="showPrice" label="Show price" valuePropName="checked">
        <Switch />
      </Form.Item>

      <Divider orientation="left" plain className="border-slate-200">
        <Text strong className="text-slate-800">
          Product or service details
        </Text>
      </Divider>

      <Form.Item noStyle dependencies={['listingType']}>
        {() => {
          const listingType = form.getFieldValue('listingType') as string;
          if (listingType === 'product') {
            return (
              <>
                <Row gutter={[16, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item name="brand" label="Brand">
                      <Input size="large" className="w-full" placeholder="Brand" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="model" label="Model">
                      <Input size="large" className="w-full" placeholder="Model" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 0]} align="bottom" className="mb-4">
                  <Col xs={24} md={10} lg={10}>
                    <Form.Item
                      name="condition"
                      label="Condition"
                      preserve={false}
                      rules={[{ required: true, message: 'Please select condition' }]}
                    >
                      <Select
                        size="large"
                        className="w-full"
                        placeholder="Search or select condition"
                        options={PRODUCT_CONDITION_OPTIONS}
                        showSearch
                        allowClear
                        optionFilterProp="label"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6} lg={6}>
                    <Form.Item name="yearOfPurchase" label="Year of purchase">
                      <InputNumber
                        size="large"
                        className="w-full"
                        min={1900}
                        max={new Date().getFullYear()}
                        placeholder="Year"
                        controls={false}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={8}>
                    <Form.Item name="warrantyAvailable" label="Warranty" valuePropName="checked">
                      <div className="flex min-h-[40px] items-center">
                        <Switch />
                      </div>
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item noStyle dependencies={['warrantyAvailable']}>
                  {() =>
                    form.getFieldValue('warrantyAvailable') ? (
                      <Row gutter={[16, 0]}>
                        <Col xs={24} md={12}>
                          <Form.Item name="warrantyDuration" label="Warranty duration">
                            <Input size="large" placeholder="e.g. 1 year" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item name="warrantyDetails" label="Warranty details">
                            <Input size="large" placeholder="Terms" />
                          </Form.Item>
                        </Col>
                      </Row>
                    ) : null
                  }
                </Form.Item>
              </>
            );
          }
          if (listingType === 'service') {
            return (
              <>
                <Form.Item
                  name="serviceType"
                  label="Service type"
                  preserve={false}
                  rules={[{ required: true, message: 'Please enter service type' }]}
                >
                  <Input size="large" className="w-full" placeholder="e.g. Repair, Installation" />
                </Form.Item>
                <Row gutter={[16, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item name="duration" label="Service duration">
                      <Input size="large" className="w-full" placeholder="e.g. 2 hours" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="availability" label="Availability">
                      <Select
                        size="large"
                        className="w-full"
                        placeholder="Search or select"
                        options={AVAILABILITY_OPTIONS}
                        showSearch
                        allowClear
                        optionFilterProp="label"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="serviceArea" label="Service area">
                  <Select mode="tags" className="w-full" placeholder="Add areas (press Enter)" />
                </Form.Item>
              </>
            );
          }
          return null;
        }}
      </Form.Item>
    </>
  );

  const locationTab = (
    <>
      <Row gutter={[16, 0]}>
        <Col xs={24} md={12}>
          <Form.Item name="address" label="Address">
            <Input size="large" className="w-full" placeholder="Address" />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item name="city" label="City">
            <Input size="large" className="w-full" placeholder="City" />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item name="state" label="State">
            <Input size="large" className="w-full" placeholder="State" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[16, 0]}>
        <Col xs={24} md={12}>
          <Form.Item name="zipCode" label="ZIP code">
            <Input size="large" className="w-full" placeholder="ZIP" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item name="showExactLocation" label="Show exact location" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="Images" required>
        <Upload
          listType="picture-card"
          multiple
          accept="image/*"
          fileList={uploadFileList}
          beforeUpload={handleBeforeUpload}
          onRemove={handleRemove}
        >
          {imageList.length >= 8 ? null : (
            <div>
              <UploadOutlined />
              <div style={{ marginTop: 8 }}>Upload</div>
            </div>
          )}
        </Upload>
      </Form.Item>
    </>
  );

  const seoTab = (
    <>
      <Form.Item name="tags" label="Tags">
        <Select mode="tags" className="w-full" placeholder="Add tags" />
      </Form.Item>
      <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Please select status' }]}>
        <Select
          size="large"
          className="w-full"
          options={STATUS_OPTIONS}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>
      <Form.Item name="expiresAt" label="Expires at">
        <DatePicker showTime className="w-full" format="YYYY-MM-DD HH:mm" />
      </Form.Item>
      <Divider orientation="left" plain className="border-slate-200">
        <Text strong className="text-slate-800">
          SEO
        </Text>
      </Divider>
      <Form.Item name="metaTitle" label="Meta title">
        <Input size="large" className="w-full" />
      </Form.Item>
      <Form.Item name="metaDescription" label="Meta description">
        <TextArea rows={3} maxLength={160} showCount className="w-full" />
      </Form.Item>
      <Form.Item name="keywords" label="Keywords">
        <Select mode="tags" className="w-full" placeholder="Keywords" />
      </Form.Item>
    </>
  );

  return (
    <PageShell
      title={mode === 'create' ? 'Create listing' : 'Edit listing'}
      description={
        mode === 'create'
          ? 'Same details as the admin listing form: products or services, pricing, location, images, and SEO. Submissions stay pending until approved.'
          : 'Update your listing. Some changes may need review before they go live.'
      }
      titleEnd={headerActions}
    >
      <Card className="vendor-surface-card w-full border border-slate-200/80 shadow-sm rounded-xl">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="vendor-comfort-form w-full max-w-full"
          requiredMark="optional"
          initialValues={LISTING_FORM_INITIAL_VALUES}
        >
          <Tabs
            defaultActiveKey="basic"
            items={[
              {
                key: 'basic',
                label: 'Basic information',
                children: (
                  <>
                    <Row gutter={[16, 0]}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="listingType"
                          label="Listing type"
                          rules={[{ required: true, message: 'Please select listing type' }]}
                        >
                          <Select
                            size="large"
                            className="w-full"
                            options={LISTING_TYPE_OPTIONS}
                            onChange={(v) => {
                              if (v === 'service') {
                                form.setFieldsValue({
                                  condition: undefined,
                                  brand: undefined,
                                  model: undefined,
                                  yearOfPurchase: undefined,
                                  warrantyAvailable: false,
                                  warrantyDuration: undefined,
                                  warrantyDetails: undefined,
                                });
                              } else {
                                form.setFieldsValue({
                                  serviceType: undefined,
                                  duration: undefined,
                                  availability: undefined,
                                  serviceArea: [],
                                });
                              }
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="itemType"
                          label="Item type"
                          rules={[{ required: true, message: 'Please select item type' }]}
                        >
                          <Select
                            size="large"
                            className="w-full"
                            options={ITEM_TYPE_OPTIONS}
                            showSearch
                            allowClear
                            optionFilterProp="label"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item
                      name="title"
                      label="Listing title"
                      rules={[
                        { required: true, message: 'Please enter a title' },
                        { min: 10, message: 'Title must be at least 10 characters' },
                        { max: 200, message: 'Title must not exceed 200 characters' },
                      ]}
                    >
                      <Input size="large" className="w-full" placeholder="Title" />
                    </Form.Item>

                    <Form.Item
                      name="description"
                      label="Description"
                      rules={[
                        {
                          validator: async (_, value) => {
                            const html = String(value ?? '').trim();
                            const plain = listingDescriptionPlainText(html);
                            if (!plain) {
                              return Promise.reject(new Error('Please enter description'));
                            }
                            if (plain.length < 50) {
                              return Promise.reject(new Error('Description must be at least 50 characters'));
                            }
                            if (html.length > 8000) {
                              return Promise.reject(new Error('Description must not exceed 8000 characters'));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <ListingDescriptionEditor />
                    </Form.Item>

                    <Row gutter={[16, 0]}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="category"
                          label="Category"
                          rules={[{ required: true, message: 'Please select category' }]}
                        >
                          <Select
                            size="large"
                            className="w-full"
                            placeholder={categoriesLoading ? 'Loading…' : 'Search or select category'}
                            options={categoryOptions}
                            loading={categoriesLoading}
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            onChange={(v) => handleCategoryChange(String(v))}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="subcategory" label="Subcategory">
                          <Select
                            size="large"
                            className="w-full"
                            placeholder="Subcategory"
                            disabled={subcategoryOptions.length === 0}
                            options={subcategoryOptions}
                            showSearch
                            allowClear
                            optionFilterProp="label"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item
                      name="store"
                      label="Store"
                      rules={[{ required: true, message: 'Please select store' }]}
                    >
                      <Select
                        size="large"
                        className="w-full"
                        placeholder={storesLoading ? 'Loading…' : 'Select an approved store'}
                        loading={storesLoading}
                        options={storeOptions}
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        disabled={mode === 'edit'}
                      />
                    </Form.Item>
                    {approvedStores.length === 0 && !storesLoading ? (
                      <Alert
                        type="warning"
                        showIcon
                        className="mb-4"
                        message="You need at least one approved store to publish listings."
                      />
                    ) : null}
                  </>
                ),
              },
              {
                key: 'pricing',
                label: 'Pricing & details',
                children: pricingDetailsTab,
              },
              {
                key: 'location',
                label: 'Location & images',
                children: locationTab,
              },
              {
                key: 'seo',
                label: 'SEO & settings',
                children: seoTab,
              },
            ]}
          />

          <Divider className="mt-6" />

          <Form.Item className="mb-0 mt-2">
            <Space wrap className="w-full justify-end" size={12}>
              <Button size="large" onClick={() => navigate('/listings')}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                size="large"
                className="border-0 bg-gradient-to-r from-[#0171BF] to-[#F7941F] !text-white hover:!text-white shadow-md hover:shadow-lg"
              >
                {mode === 'create' ? 'Create listing' : 'Update listing'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </PageShell>
  );
};

export default ListingForm;
